import React, { useState, useEffect, useCallback } from 'react'
import { XMarkIcon, ArrowUpOnSquareIcon, ExclamationTriangleIcon, CheckCircleIcon, BugAntIcon, SparklesIcon, HeartIcon } from '@heroicons/react/24/outline'
import useUpdateCheck from '../hooks/useUpdateCheck'

const UpdateNotification = () => {
    const [isDismissed, setIsDismissed] = useState(false)
    const [latestChangelog, setLatestChangelog] = useState(null)
    const [loadingChangelog, setLoadingChangelog] = useState(false)

    const {
        hasUpdate,
        currentVersion,
        latestVersion,
        lastCheckTime,
        isInitializing
    } = useUpdateCheck()

    // 获取最新版本的更新日志（使用 useCallback 确保依赖正确）
    const fetchLatestChangelog = useCallback(async () => {
        if (!latestVersion) {
            console.warn('⚠️ latestVersion 为空，无法获取更新日志')
            return
        }

        try {
            setLoadingChangelog(true)
            // 添加时间戳参数防止缓存
            const timestamp = Date.now()
            const response = await fetch(`/api/latest-changelog?t=${timestamp}`, {
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            })

            if (response.ok) {
                const data = await response.json()
                console.log('📋 获取最新 changelog 数据:', {
                    totalVersions: data.versions?.length,
                    latestVersion: latestVersion,
                    firstVersion: data.versions?.[0]?.version
                })

                // 查找匹配最新版本的更新日志
                const versionLog = data.versions?.find(v => v.version === latestVersion)
                setLatestChangelog(versionLog)

                if (versionLog) {
                    console.log('✅ 成功加载更新日志:', versionLog.title)
                } else {
                    console.warn('⚠️ 未找到版本 ' + latestVersion + ' 的更新日志')
                    console.log('可用的版本:', data.versions?.map(v => v.version).join(', '))
                }
            } else {
                console.error('❌ 获取更新日志失败: HTTP', response.status)
            }
        } catch (error) {
            console.error('❌ 获取更新日志失败:', error)
        } finally {
            setLoadingChangelog(false)
        }
    }, [latestVersion])

    // 检查本地存储的dismissed状态
    useEffect(() => {
        const dismissed = localStorage.getItem('update-dismissed')
        const dismissTime = localStorage.getItem('update-dismiss-time')

        if (dismissed && dismissTime) {
            const timeDiff = Date.now() - parseInt(dismissTime)
            // 24小时后重新显示
            if (timeDiff < 24 * 60 * 60 * 1000) {
                setIsDismissed(true)
            } else {
                localStorage.removeItem('update-dismissed')
                localStorage.removeItem('update-dismiss-time')
                setIsDismissed(false)
            }
        }
    }, [])

    // 当检测到新版本时，重置忽略状态并获取更新日志
    useEffect(() => {
        console.log('🔄 UpdateNotification useEffect 触发:', {
            hasUpdate,
            isInitializing,
            latestVersion,
            currentVersion,
            latestChangelogExists: !!latestChangelog,
            isDismissed,
            debugMode
        })

        if (hasUpdate && !isInitializing && latestVersion) {
            const currentDismissedVersion = localStorage.getItem('dismissed-version')

            console.log('📌 版本检查:', {
                currentDismissedVersion,
                latestVersion,
                isDifferent: currentDismissedVersion !== latestVersion
            })

            // 如果是新的版本，重置忽略状态
            if (currentDismissedVersion !== latestVersion) {
                console.log('🆕 发现新版本，重置忽略状态')
                setIsDismissed(false)
                localStorage.removeItem('update-dismissed')
                localStorage.removeItem('update-dismiss-time')
            }

            // 总是获取最新版本的更新日志（即使已经有了，也重新获取以确保是最新的）
            console.log('📥 开始获取更新日志...')
            fetchLatestChangelog()
        }
    }, [hasUpdate, latestVersion, isInitializing, fetchLatestChangelog])

    // 获取更新类型对应的图标
    const getChangeTypeIcon = (type) => {
        switch (type) {
            case 'feature':
                return <SparklesIcon className="h-4 w-4 text-green-400" />
            case 'bugfix':
                return <BugAntIcon className="h-4 w-4 text-red-400" />
            case 'improvement':
                return <CheckCircleIcon className="h-4 w-4 text-blue-400" />
            case 'security':
                return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-400" />
            case 'thanks':
                return <HeartIcon className="h-4 w-4 text-pink-400" />
            default:
                return <CheckCircleIcon className="h-4 w-4 text-gray-400" />
        }
    }

    // 关闭通知
    const dismissNotification = () => {
        setIsDismissed(true)
        localStorage.setItem('update-dismissed', 'true')
        localStorage.setItem('update-dismiss-time', Date.now().toString())
        localStorage.setItem('dismissed-version', latestVersion) // 记录忽略的版本
    }

    // 调试模式：即使没有更新也显示通知（用于测试）
    const debugMode = localStorage.getItem('update-debug-mode') === 'true'

    // 只有在有更新且未被忽略时才显示通知（除非是调试模式）
    if ((!hasUpdate && !debugMode) || isDismissed || isInitializing) {
        return null
    }

    return (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg shadow-xl border border-blue-400 p-4 animate-in slide-in-from-top-2 duration-300">
                {/* 头部 */}
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                        <div className="bg-white bg-opacity-20 rounded-full p-1">
                            <ArrowUpOnSquareIcon className="h-5 w-5" />
                        </div>
                        <h3 className="font-semibold text-lg">发现新版本！</h3>
                    </div>
                    <button
                        onClick={dismissNotification}
                        className="text-white hover:text-gray-200 transition-colors p-1 rounded"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* 版本信息 */}
                <div className="space-y-2 mb-4">
                    <div className="bg-white bg-opacity-10 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium">当前版本</span>
                            <span className="bg-white bg-opacity-20 px-2 py-1 rounded text-sm font-mono">
                                v{currentVersion}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">最新版本</span>
                            <span className="bg-green-500 bg-opacity-80 px-2 py-1 rounded text-sm font-mono font-semibold">
                                v{latestVersion}
                            </span>
                        </div>
                    </div>
                </div>

                {/* 最新版本更新内容 */}
                {latestChangelog && (
                    <div className="bg-white bg-opacity-10 rounded-lg p-3 mb-4">
                        <div className="flex items-center space-x-2 mb-2">
                            <h4 className="text-sm font-semibold">{latestChangelog.title}</h4>
                            <span className="text-xs text-white text-opacity-70">
                                {latestChangelog.date}
                            </span>
                        </div>
                        <div className="space-y-2">
                            {latestChangelog.changes?.map((change, index) => (
                                <div key={index} className="flex items-start space-x-2">
                                    <div className="mt-0.5 flex-shrink-0">
                                        {getChangeTypeIcon(change.type)}
                                    </div>
                                    <div className="flex-1">
                                        <span className="text-xs text-white text-opacity-90 leading-relaxed whitespace-pre-line">
                                            {change.description}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 加载状态 */}
                {loadingChangelog && (
                    <div className="bg-white bg-opacity-10 rounded-lg p-3 mb-4">
                        <div className="flex items-center justify-center space-x-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                            <span className="text-sm text-white text-opacity-70">加载更新内容...</span>
                        </div>
                    </div>
                )}

                {/* 更新提示 */}
                <div className="bg-white bg-opacity-10 rounded-lg p-3 mb-4">
                    <div className="flex items-start space-x-2">
                        <ExclamationTriangleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                            <p className="font-medium mb-1">如何更新：</p>
                            <ol className="text-xs space-y-1 list-decimal list-inside">
                                <li>停止当前容器: <code className="bg-black bg-opacity-20 px-1 rounded">docker-compose down</code></li>
                                <li>拉取最新镜像: <code className="bg-black bg-opacity-20 px-1 rounded">docker-compose pull</code></li>
                                <li>重新启动: <code className="bg-black bg-opacity-20 px-1 rounded">docker-compose up -d</code></li>
                            </ol>
                        </div>
                    </div>
                </div>

                {/* 操作按钮 */}
                <div className="flex space-x-2">
                    <a
                        href="https://hub.docker.com/r/kwangyeonc/homepage-config-manager"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-center py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 border border-white border-opacity-30"
                    >
                        查看详情
                    </a>
                    <button
                        onClick={dismissNotification}
                        className="flex-1 bg-white bg-opacity-20 hover:bg-opacity-30 py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 border border-white border-opacity-30"
                    >
                        稍后提醒
                    </button>
                </div>

                {/* 调试信息 */}
                {debugMode && (
                    <div className="mt-3 p-2 bg-black bg-opacity-20 rounded text-xs">
                        <div className="text-yellow-300 mb-1">🐛 调试模式</div>
                        <div>当前版本: {currentVersion}</div>
                        <div>最新版本: {latestVersion}</div>
                        <div>有更新: {hasUpdate ? '是' : '否'}</div>
                        <div>更新日志: {latestChangelog ? '已加载' : '未加载'}</div>
                        <div>加载状态: {loadingChangelog ? '加载中' : '已完成'}</div>
                        {latestChangelog && (
                            <div className="mt-1">
                                <div>标题: {latestChangelog.title}</div>
                                <div>日期: {latestChangelog.date}</div>
                                <div>变更数量: {latestChangelog.changes?.length || 0}</div>
                            </div>
                        )}
                        <button
                            onClick={() => {
                                localStorage.removeItem('update-debug-mode')
                                window.location.reload()
                            }}
                            className="mt-1 text-blue-300 hover:text-blue-200 underline"
                        >
                            关闭调试模式
                        </button>
                    </div>
                )}

                {/* 最后检查时间 */}
                {lastCheckTime && (
                    <div className="text-xs text-white text-opacity-70 mt-2 text-center">
                        最后检查: {lastCheckTime.toLocaleTimeString()}
                    </div>
                )}
            </div>
        </div>
    )
}

export default UpdateNotification